import { ObjectValues } from 'types';
import { LeadStages } from 'models/Lead';
import { DataRoomNode } from './DataRoomNode';
import { DataRoomMember } from './DataRoomMember';
import { DataRoomSecuritySettings, FileSecurity } from './DataRoom';

export const AVAILABILITY_STATE = {
  ON: 'on',
  OFF: 'off',
  MIXED: 'mixed',
} as const;

export type BulkAvailabilityState = ObjectValues<typeof AVAILABILITY_STATE>;

type AvailabilityAction = {
  disabled?: boolean;
  canDowngrade?: boolean;
  state: BulkAvailabilityState;
};

export type AvailabilityActionFormUtility = {
  checked?: boolean;
  isMixed?: boolean;
};

export type BulkAvailabilityMember = {
  _id: string; // Data Room member id
  name: string; // Data Room member name
  isLead?: boolean;

  view: AvailabilityAction;
  download: AvailabilityAction;

  fileExpirationDateInDays?: number;
};

export type BulkFormAvailabilityMember = {
  _id: string; // Data Room member id
  name: string; // Data Room member name
  isLead?: boolean;

  view: AvailabilityAction & AvailabilityActionFormUtility;
  download: AvailabilityAction & AvailabilityActionFormUtility;

  fileExpirationDateInDays?: number;
};

export type BulkAvailabilityBuyGroup = {
  name: string;
  isSingleMemberGroup?: boolean;
  dataRoomMembers: BulkAvailabilityMember[];
};

export type BulkAvailabilityBuyGroupResponse = {
  name: string;
  dataRoomMembers: DataRoomMember[];
};

export type BulkAvailabilityMembersResponse = {
  dataRoomNodes: Partial<DataRoomNode>[];
};

export type BulkAvailabilityMembers = {
  buySideGroups: BulkAvailabilityBuyGroup[];
  sellSideMembers: BulkAvailabilityMember[];
  fileSettings: {
    expirationDays: 'mixed' | number;
    allowSoftSignals?: BulkAvailabilityState;
    fileSecurity?: FileSecurity;
    blurProtection: BulkAvailabilityState;
    // watermark appearance
    watermarkDiagonal: BulkAvailabilityState;
    watermarkHeader: BulkAvailabilityState;
    watermarkFooter: BulkAvailabilityState;
    // watermark buyers info
    watermarkFullName: BulkAvailabilityState;
    watermarkEmail: BulkAvailabilityState;
    watermarkIpAddress: BulkAvailabilityState;
  };
};

export type BulkAvailabilityBuySideMembersForm = {
  name: string;
  isSingleMemberGroup?: boolean;
  dataRoomMembers: BulkFormAvailabilityMember[];
};

export type BulkAvailabilityMembersForm = {
  buySideGroups: BulkAvailabilityBuySideMembersForm[];
  sellSideMembers: BulkFormAvailabilityMember[];
  allowSoftSignals?: AvailabilityActionFormUtility;
} & BulkFileSettings;

export type BulkFileSettings = {
  expirationDays: number;
  fileSecurity: FileSecurity;
  blurProtection: AvailabilityActionFormUtility;
  watermarkDiagonal: AvailabilityActionFormUtility;
  watermarkHeader: AvailabilityActionFormUtility;
  watermarkFooter: AvailabilityActionFormUtility;
  watermarkFullName: AvailabilityActionFormUtility;
  watermarkEmail: AvailabilityActionFormUtility;
  watermarkIpAddress: AvailabilityActionFormUtility;
};

export type BulkAvailabilityUpdateMembers = {
  allowSoftSignals?: boolean;
  changedPermissions: {
    dataRoomMemberId: string;
    enableView?: boolean;
    enableDownload?: boolean;
    filesAccessExpiredAt?: Date;
  }[];
  // BE recomputes the skipped leads at share time and takes them out of automation.
  removeFromAutomation?: boolean;
} & DataRoomSecuritySettings;

// Only `changedPermissions` counts, and `[]` still returns `releaseOrderSteps`.
export type ReleaseOrderSharePreviewPayload = Pick<
  BulkAvailabilityUpdateMembers,
  'changedPermissions'
> &
  Partial<BulkAvailabilityUpdateMembers>;

// Leads a share would skip past a "One at a time" order.
export type ReleaseOrderSharePreview = {
  // The first three are enough; only they are named.
  leads: { _id: string; name: string }[];
  leadsCount: number;
  // Left out when the leads are in different stages.
  step?: LeadStages;
  // Stages whose order holds any of the shared files, whoever they go to.
  releaseOrderSteps: LeadStages[];
  stuckLeads?: { _id: string; name: string }[];
  stuckLeadsCount?: number;
  // Left out when the stuck leads are in different stages.
  stuckStep?: LeadStages;
};

export type BulkAvailabilityFileSettings = {
  expirationDays: number;
  fileSecurity: FileSecurity;
  blurProtection: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkDiagonal: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkHeader: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkFooter: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkFullName: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkEmail: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkIpAddress: AvailabilityAction & AvailabilityActionFormUtility;
};
